import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useSelector} from 'react-redux';
import {subMonths} from 'date-fns';

import IItem from '../../types/product_delivery';
import {ExtendedDocument} from '../../types/DocumentsTypes/Document';
import {createDocument} from '../../types/DocumentsTypes/utils';
import {dateObjectToString, roundDecimals} from '../../fromKotlin/nkutils';
import {selectActiveCustomers} from '../../store/customers/customersSlice';
import {selectActiveSuppliers} from '../../store/suppliers/suppliersSlice';
import {selectUser} from '../../store/user/userSlice';
import {getSpecificDocument} from '../../api/customer/documents.api';
import {
    useCreateCustomerDocument,
    useUpdateCustomerDocument,
} from '../../hooks/customer/documents.hooks';
import {
    getCustomerProductsPrices,
    getSupplierProductsPrices
} from '../../api/customer/products_prices.api';
import JSDB from '../../fromKotlin/nk';
import {
    extractDocumentFromText,
    matchExtractedProducts,
    runSmartDocumentExtraction,
    TextToDataAiDebugEvent,
    TEXT_TO_DATA_MATCH_BACKEND
} from './aiClient';
import type {SmartExtractionSuggested} from './aiClient';
import {
    applyAiMatches,
    buildProductCandidates,
    isCatalogProductAvailable,
    matchedLinesToItems,
    summarizePreviousDocumentProducts
} from './matching';
import {
    aiConfirmMatches,
    aiMatchProductsStream,
    aiMatchSupplierProductsStream,
} from '../../api/customer/ai_assistent.api';
import type {
    MatchConfirmation,
    SmartMatchMatchesEvent,
} from '../../api/customer/ai_assistent.api';
import {
    ExtractedDocument,
    MatchedProductLine,
    ProductMatchCandidate,
    TextToDataEntityOption,
    TextToDataProcess,
    TextToDataStage
} from './types';

export interface TextToDataDrawerAiProps {
    open: boolean;
    onClose: () => void;
    /** Default document date inside the drawer. */
    defaultDate: Date;
    /** Called after a new order / supplier invoice is created or merged. */
    onDocumentCreated?: (document: ExtendedDocument) => void;
    /** Optional: open with this process pre-selected. */
    initialProcess?: TextToDataProcess;
    /** Optional: pre-select client/supplier by ERP id. */
    initialEntityId?: string | number | null;
}

const DRAWER_WIDTH = 1480;
const LEFT_PANEL_WIDTH = 460;
const LOW_CONFIDENCE = 0.75;
/** localStorage key — האם לשמור את הטקסט המקורי של כל שורה כהערה במוצר. ערכים: 'yes' / 'no' */
const NOTES_PREF_STORAGE_KEY = 'textToDataAi.saveSourceAsNotes';
/** כש־'yes' — מוצרים עם available=0 לא נשלחים ל-AI כמועמדים */
const IGNORE_UNAVAILABLE_STORAGE_KEY = 'textToDataAi.ignoreUnavailable';
/** מנוע התאמת מוצרים — ערוך ב־`aiClient.ts` את `TEXT_TO_DATA_MATCH_BACKEND` (מוצג גם בדיבאג) */

const processLabels: Record<TextToDataProcess, string> = {
    order: 'קליטת הזמנה',
    supplier_invoice: 'קליטת חשבוניות ספק',
};

const rtlInputSx = {
    '& .MuiInputBase-input': {
        direction: 'rtl',
        textAlign: 'right',
    },
    '& .MuiInputLabel-root': {
        right: 28,
        left: 'auto',
        transformOrigin: 'top right',
    },
} as const;

const summarizeProductForDebug = (product: IItem) => ({
    id: product.id,
    name: product.name,
    available_units: product.available_units,
    barcode: product.barcode,
    priority: product.priority,
});

/** Table rows right after step-1 extract, before AI product matching. */
const buildProvisionalMatchesFromExtracted = (extractedDoc: ExtractedDocument): MatchedProductLine[] =>
    extractedDoc.products.map((line) => ({
        lineId: line.lineId,
        extracted: line,
        candidates: [],
        amount: line.amount ?? 0,
        unitIndex: 0,
        price: line.price ?? undefined,
        confidence: line.confidence ?? 0,
        confirmed: false,
    }));

const snapshotExtractionInputFields = (extractedDoc: ExtractedDocument) => ({
    hadBarcode: extractedDoc.products.some((p) => Boolean(p.barcode != null && String(p.barcode).trim())),
    hadExtId: extractedDoc.products.some((p) => Boolean(p.extId != null && String(p.extId).trim())),
});

const sortCandidatesCatalogAvailableFirst = (candidates: ProductMatchCandidate[]) =>
    [...candidates].sort((a, b) => {
        const ra = a.catalogAvailable === false ? 0 : 1;
        const rb = b.catalogAvailable === false ? 0 : 1;
        if (rb !== ra) return rb - ra;
        return a.name.localeCompare(b.name, 'he');
    });

const normalizeForCompare = (value: string) =>
    (value ?? '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\"'׳״.,;:()[\]{}]/g, ' ')
        .replace(/\s+/g, ' ');

const safeDebugStringify = (value: any) => {
    const seen = new WeakSet();
    try {
        return JSON.stringify(value, (key, currentValue) => {
            if (typeof currentValue === 'function') return `[Function ${currentValue.name || 'anonymous'}]`;
            if (typeof currentValue === 'object' && currentValue !== null) {
                if (seen.has(currentValue)) return '[Circular]';
                seen.add(currentValue);
            }
            return currentValue;
        }, 2);
    } catch (error) {
        return String(value);
    }
};

const toEntityOption = (entity: any): TextToDataEntityOption => ({
    id: String(entity?.id ?? ''),
    name: entity?.name ?? entity?.businessName ?? '',
    businessName: entity?.businessName,
    externalId: entity?.externalId ?? entity?.external_id,
    raw: entity,
});

const readFileAsText = async (file: File) => {
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        const buffer = await file.arrayBuffer();
        const bytes = Array.from(new Uint8Array(buffer));
        const binary = bytes.map((byte) => String.fromCharCode(byte)).join('');
        return `PDF file name: ${file.name}\nBase64 PDF content:\n${btoa(binary)}`;
    }
    return file.text();
};

/** Read any file → raw base64 (no `data:` prefix). Used by the smart extractor
 *  which sends the bytes straight to the server (vision model rasterizes PDFs). */
const readFileAsBase64 = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    // Chunk to avoid argument-length blow-up on large PDFs / images.
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
    }
    return btoa(binary);
};

/** Classify the upload so the drawer knows which path (text / image / pdf) to call. */
type UploadedKind = 'text' | 'image' | 'pdf';
const classifyFile = (file: File): UploadedKind => {
    const name = file.name.toLowerCase();
    if (file.type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (file.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic)$/i.test(name)) return 'image';
    return 'text';
};

const createDetailsFromEntity = (entity?: TextToDataEntityOption) => ({
    city: entity?.raw?.city ?? '',
    companyId: entity?.raw?.businessId ?? entity?.raw?.business_id ?? '',
    companyName: entity?.businessName ?? entity?.name ?? '',
    email: entity?.raw?.email ?? '',
    id: Number(entity?.id ?? 0),
    phone: entity?.raw?.phone ?? entity?.raw?.phone1 ?? '',
    street: entity?.raw?.street ?? '',
    streetNumber: entity?.raw?.streetNumber ?? '',
});

const calcDocumentTotals = (items: IItem[], extracted?: ExtractedDocument) => {
    const productsValue = items.reduce((sum, item) => {
        const amount = (item.quantity ?? 0) + (item.quantitySecond ?? 0);
        return sum + amount * Number(item.totalPrice ?? 0);
    }, 0);
    const total = Number(extracted?.total ?? productsValue);
    return {
        mainValue: roundDecimals(total, 4),
        preValue: roundDecimals(total, 4),
        taxValue: 0,
    };
};

export default function TextToDataDrawerAi({
    open,
    onClose,
    defaultDate,
    onDocumentCreated,
    initialProcess = 'order',
    initialEntityId = null,
}: TextToDataDrawerAiProps) {
    const rawCustomers = useSelector(selectActiveCustomers);
    const rawSuppliers = useSelector(selectActiveSuppliers);
    const loggedUser = useSelector(selectUser);
    const customers = React.useMemo(
        () => rawCustomers.map(toEntityOption),
        [rawCustomers],
    );
    const suppliers = React.useMemo(
        () => rawSuppliers.map(toEntityOption),
        [rawSuppliers],
    );

    // ── Self-company hint ───────────────────────────────────────────────────
    // Tells the LLM extractor "this is US on the document — pick the OTHER
    // party as `entity`". Without this, PDFs that print both supplier + client
    // can mis-identify our own letterhead as the counterparty.
    const selfCompanyHint = React.useMemo(() => {
        if (!loggedUser) return undefined;
        const name = String(loggedUser.companyName ?? '').trim();
        const taxId = String(loggedUser.companyId ?? '').trim();
        const aliases: string[] = [];
        const pushAlias = (v: unknown) => {
            const s = String(v ?? '').trim();
            if (s && s !== name && !aliases.includes(s)) aliases.push(s);
        };
        pushAlias((loggedUser as { businessName?: string }).businessName);
        pushAlias((loggedUser as { brandName?: string }).brandName);
        pushAlias((loggedUser as { tradeName?: string }).tradeName);
        if (!name && !taxId && aliases.length === 0) return undefined;
        return {
            name:    name || undefined,
            tax_id:  taxId || undefined,
            aliases,
        };
    }, [loggedUser]);

    const [process, setProcess] = React.useState<TextToDataProcess>('order');
    const [entity, setEntity] = React.useState<TextToDataEntityOption | null>(null);
    const [date, setDate] = React.useState(defaultDate);
    const [inputText, setInputText] = React.useState('');
    const [stage, setStage] = React.useState<TextToDataStage>('idle');
    const [error, setError] = React.useState<string | null>(null);
    const [extracted, setExtracted] = React.useState<ExtractedDocument | null>(null);
    const [matches, setMatches] = React.useState<MatchedProductLine[]>([]);
    const [contextProducts, setContextProducts] = React.useState<IItem[]>([]);
    const [previousDocumentsPreview, setPreviousDocumentsPreview] = React.useState<ExtendedDocument[]>([]);
    // Smart mode is "the server already did the heavy lifting". We skip
    // pre-fetching the catalog + previous documents (the server pulled them
    // for matching), and only fetch the IItem details we genuinely need
    // (the matched products) AFTER the resolve response. previousDocsLoaded
    // tracks whether the cosmetic side-panel preview was lazy-loaded yet.
    const [previousDocsLoaded, setPreviousDocsLoaded] = React.useState<boolean>(false);
    const [previousDocsLoading, setPreviousDocsLoading] = React.useState<boolean>(false);
    // The entity that was actually used for the current match run (may differ
    // from `entity` if the server auto-resolved it from the document header).
    // Used by the lazy "Load previous documents" button.
    const [activeEntityForContext, setActiveEntityForContext] = React.useState<TextToDataEntityOption | null>(null);
    const [manualProduct, setManualProduct] = React.useState<IItem | null>(null);
    const [manualAmount, setManualAmount] = React.useState<number>(1);
    const [manualUnitIndex, setManualUnitIndex] = React.useState<number>(0);
    const [manualPrice, setManualPrice] = React.useState<number>(0);
    const [debugOpen, setDebugOpen] = React.useState(false);
    const [debugEvents, setDebugEvents] = React.useState<TextToDataAiDebugEvent[]>([]);
    const [sameDayDocument, setSameDayDocument] = React.useState<ExtendedDocument | null>(null);
    const [updatePromptOpen, setUpdatePromptOpen] = React.useState(false);
    const [saveSourceAsNotes, setSaveSourceAsNotes] = React.useState<boolean>(() => {
        try {
            const stored = localStorage.getItem(NOTES_PREF_STORAGE_KEY);
            return stored === null ? true : stored === 'yes';
        } catch {
            return true;
        }
    });
    const [notesPrefSet, setNotesPrefSet] = React.useState<boolean>(() => {
        try {
            return localStorage.getItem(NOTES_PREF_STORAGE_KEY) !== null;
        } catch {
            return false;
        }
    });
    const [notesPrefDialogOpen, setNotesPrefDialogOpen] = React.useState(false);
    const [ignoreUnavailableProducts, setIgnoreUnavailableProducts] = React.useState<boolean>(() => {
        try {
            const stored = localStorage.getItem(IGNORE_UNAVAILABLE_STORAGE_KEY);
            return stored === null ? true : stored === 'yes';
        } catch {
            return true;
        }
    });
    /** Smart server-side pipeline (extract+resolve, supports image/PDF, server builds candidates).
     *  Default ON — the server-side flow is the production path now. */
    const [useSmartExtraction, setUseSmartExtraction] = React.useState<boolean>(true);
    /** Streams extract → resolve from the server. Lights up the table immediately
     *  after extraction, then fills the match column when resolve returns —
     *  feels much snappier on multi-page PDFs. Default ON. */
    const [useStreamingMatch, setUseStreamingMatch] = React.useState<boolean>(true);
    /** Holds the binary upload (image/PDF) when user attaches a file. text path keeps using inputText. */
    const [uploadedBinary, setUploadedBinary] = React.useState<{
        kind: 'image' | 'pdf';
        fileName: string;
        base64: string;
        mimeType: string;
    } | null>(null);
    /** Preflight summary for text/CSV uploads (name + line count), shown before AI analysis runs so
     * users can sanity-check the file loaded correctly. Cleared on manual edit/clear so it never goes stale. */
    const [uploadedTextFileInfo, setUploadedTextFileInfo] = React.useState<{fileName: string; lineCount: number} | null>(null);
    /** Suggested header fields the server extracted (date / allocation / doc_id / entity). Read-only banner. */
    const [smartSuggested, setSmartSuggested] = React.useState<SmartExtractionSuggested | null>(null);
    /** Which identifier columns appeared in step-1 extraction (from source input, not user edits). */
    const [extractionInputFields, setExtractionInputFields] = React.useState<{hadBarcode: boolean; hadExtId: boolean}>({
        hadBarcode: false,
        hadExtId: false,
    });

    const persistIgnoreUnavailableProducts = React.useCallback((value: boolean) => {
        setIgnoreUnavailableProducts(value);
        try {
            localStorage.setItem(IGNORE_UNAVAILABLE_STORAGE_KEY, value ? 'yes' : 'no');
        } catch {
            // ignore
        }
    }, []);

    const persistSaveSourceAsNotes = React.useCallback((value: boolean) => {
        setSaveSourceAsNotes(value);
        setNotesPrefSet(true);
        try {
            localStorage.setItem(NOTES_PREF_STORAGE_KEY, value ? 'yes' : 'no');
        } catch {
            // ignore storage errors (private mode etc.)
        }
    }, []);

    const entityOptions = process === 'order' ? customers : suppliers;
    const entityIdStr = entity?.id ? String(entity.id) : '';
    const createOrderDocument = useCreateCustomerDocument(entityIdStr);
    const createSupplierInvoiceDocument = useCreateCustomerDocument(entityIdStr, true);
    const updateClientDocument = useUpdateCustomerDocument('client', 0);
    const updateSupplierDocument = useUpdateCustomerDocument('supplier', 0);
    const isBusy = ['extracting', 'loading-context', 'matching', 'creating'].includes(stage)
        || createOrderDocument.isLoading
        || createSupplierInvoiceDocument.isLoading
        || updateClientDocument.isLoading
        || updateSupplierDocument.isLoading;
    const canCreate = entity && matches.length > 0 && matches.every((line) => line.matchedProduct && line.amount > 0 && (line.confirmed || line.confidence >= LOW_CONFIDENCE));
    const conversionRateBlocker = Number(JSDB().userCofcConf?.conversion_rate_blocker ?? 0);

    const wasOpenRef = React.useRef(false);
    React.useEffect(() => {
        const justOpened = open && !wasOpenRef.current;
        wasOpenRef.current = open;
        if (!justOpened) return;

        setDate(defaultDate);
        setProcess(initialProcess);
        if (initialEntityId != null && String(initialEntityId).trim() !== '') {
            const opts = initialProcess === 'supplier_invoice' ? suppliers : customers;
            const found = opts.find((e) => String(e.id) === String(initialEntityId));
            setEntity(found ?? null);
        } else {
            setEntity(null);
        }
    }, [open, defaultDate, initialProcess, initialEntityId, customers, suppliers]);

    const resetAfterProcessChange = React.useCallback(() => {
        setEntity(null);
        setExtracted(null);
        setMatches([]);
        setContextProducts([]);
        setPreviousDocumentsPreview([]);
        setPreviousDocsLoaded(false);
        setPreviousDocsLoading(false);
        setActiveEntityForContext(null);
        setSameDayDocument(null);
        setManualProduct(null);
        setManualAmount(1);
        setManualUnitIndex(0);
        setManualPrice(0);
        setError(null);
        setExtractionInputFields({hadBarcode: false, hadExtId: false});
    }, []);

    // Full reset every time the drawer closes — next open starts blank.
    React.useEffect(() => {
        if (open) return;
        setProcess('order');
        setEntity(null);
        setInputText('');
        setExtracted(null);
        setMatches([]);
        setContextProducts([]);
        setPreviousDocumentsPreview([]);
        setPreviousDocsLoaded(false);
        setPreviousDocsLoading(false);
        setActiveEntityForContext(null);
        setSameDayDocument(null);
        setManualProduct(null);
        setManualAmount(1);
        setManualUnitIndex(0);
        setManualPrice(0);
        setError(null);
        setStage('idle');
        setDebugEvents([]);
        setUpdatePromptOpen(false);
        setUploadedBinary(null);
        setSmartSuggested(null);
        setExtractionInputFields({hadBarcode: false, hadExtId: false});
    }, [open]);

    const updateLine = (lineId: string, patch: Partial<MatchedProductLine>) => {
        setMatches((prev) => prev.map((line) => line.lineId === lineId ? {...line, ...patch} : line));
    };

    const removeLine = (lineId: string) => {
        setMatches((prev) => prev.filter((line) => line.lineId !== lineId));
    };

    const addDebugEvent = (event: TextToDataAiDebugEvent) => {
        setDebugEvents((prev) => [...prev, {...event, data: event.data}]);
    };

    const loadContextFor = async (targetEntity: TextToDataEntityOption) => {
        const dateString = dateObjectToString(date);
        const fromDate = subMonths(date, 8);

        if (process === 'order') {
            const [products, documents] = await Promise.all([
                getCustomerProductsPrices(targetEntity.id, dateString, true, undefined, false, true),
                getSpecificDocument(
                    'order',
                    Number(targetEntity.id),
                    fromDate,
                    date,
                    undefined,
                    undefined,
                    true,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    3
                ),
            ]);
            return {products, previousDocuments: documents};
        }

        const [products, supplierNotes, supplierInvoices] = await Promise.all([
            getSupplierProductsPrices(targetEntity.id, dateString, true),
            getSpecificDocument(
                'supplier_note',
                Number(targetEntity.id),
                fromDate,
                date,
                undefined,
                undefined,
                true,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                3
            ),
            getSpecificDocument(
                'sup_invoice',
                Number(targetEntity.id),
                fromDate,
                date,
                undefined,
                undefined,
                true,
                true,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                3
            ),
        ]);
        const documents = [...supplierNotes, ...supplierInvoices]
            .filter((doc: any) => Array.isArray(doc.products) && doc.products.length > 0)
            .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 3);
        return {products, previousDocuments: documents};
    };

    const loadContext = async () => {
        if (!entity) throw new Error('בחר לקוח או ספק לפני הפעלה');
        return loadContextFor(entity);
    };

    // ── Smart-mode scoped fetchers ─────────────────────────────────────────
    // In smart mode the server already pulled the catalog / previous orders
    // for matching, so we don't refetch the whole pricelist on the client.
    // We only need IItem details for the products that were actually matched
    // (typically 5-50 rows). The server returns productId, productName, unit,
    // amount and price per line; the full IItem is required for spread into
    // the final document (taxRate, category, supplier_id, conversion_ratio,
    // available_units, etc).
    const loadMatchedProductsOnly = async (
        targetEntity: TextToDataEntityOption,
        matchedIds: number[],
    ): Promise<IItem[]> => {
        if (!matchedIds.length) return [];
        const dateString = dateObjectToString(date);
        if (process === 'order') {
            // Client API supports an ids filter — server only computes
            // pricing/priority for those ids, much smaller than full catalog.
            return getCustomerProductsPrices(
                targetEntity.id, dateString, true, matchedIds, false, true,
            );
        }
        // Supplier API doesn't take an ids filter; fetch + filter locally.
        // Still cheaper than running both extract+resolve again, and we only
        // pay this once per analyze (not before AND after like loadContextFor).
        const all = await getSupplierProductsPrices(targetEntity.id, dateString, true);
        const wanted = new Set(matchedIds.map((n) => Number(n)));
        return all.filter((item) => wanted.has(Number(item.id)));
    };

    // Lazy-load previous documents for the side-panel preview. Called only
    // when the user clicks "טען מסמכים אחרונים" in smart mode (server already
    // used them server-side, so this fetch is purely cosmetic).
    const loadPreviousDocumentsForSidePanel = async (
        targetEntity: TextToDataEntityOption,
    ) => {
        if (previousDocsLoading) return;
        setPreviousDocsLoading(true);
        try {
            const ctx = await loadContextFor(targetEntity);
            setPreviousDocumentsPreview((ctx.previousDocuments ?? []).slice(0, 3));
            setPreviousDocsLoaded(true);
        } catch (err) {
            addDebugEvent({
                stage: 'context',
                title: 'שגיאה בטעינת מסמכים אחרונים (cosmetic)',
                data: String(err),
            });
        } finally {
            setPreviousDocsLoading(false);
        }
    };

    const knownUnitsFromProducts = (products: IItem[]) => {
        const units = new Set<string>();
        products.forEach((product) => {
            (product.available_units ?? []).forEach((unit) => {
                if (unit) units.add(unit);
            });
            if (product.unit) units.add(product.unit);
        });
        return Array.from(units);
    };

    const handleManualProductChange = (product: IItem | null) => {
        setManualProduct(product);
        setManualUnitIndex(0);
        setManualPrice(Number(product?.totalPrice ?? 0));
    };

    const handleAddManualProduct = () => {
        if (!manualProduct || manualAmount <= 0) return;
        const lineId = `manual_${Date.now()}`;
        setMatches((prev) => [
            ...prev,
            {
                lineId,
                extracted: {
                    lineId,
                    productName: manualProduct.name,
                    amount: manualAmount,
                    unit: manualProduct.available_units?.[manualUnitIndex] ?? manualProduct.unit ?? null,
                    price: manualPrice,
                    barcode: manualProduct.barcode ?? null,
                    extId: manualProduct.external_id ?? null,
                    confidence: 1,
                    rawText: 'manual',
                },
                candidates: [{
                    productId: Number(manualProduct.id),
                    name: manualProduct.name,
                    unit: manualProduct.unit,
                    availableUnits: manualProduct.available_units,
                    catalogAvailable: isCatalogProductAvailable(manualProduct),
                    price: manualProduct.totalPrice,
                    barcode: manualProduct.barcode,
                    externalId: manualProduct.external_id,
                    priority: manualProduct.priority,
                    score: 1,
                }],
                matchedProduct: manualProduct,
                amount: manualAmount,
                unitIndex: manualUnitIndex,
                price: manualPrice,
                confidence: 1,
                confirmed: true,
                reason: 'נוסף ידנית',
            },
        ]);
        setManualProduct(null);
        setManualAmount(1);
        setManualUnitIndex(0);
        setManualPrice(0);
    };

    const buildMatchConfirmations = (): MatchConfirmation[] => {
        const rowsByKey = new Map<string, MatchConfirmation>();
        const entityType = process === 'supplier_invoice' ? 'supplier_product' : 'product';
        const buildLineMemoryMetadata = (line: MatchedProductLine, product: IItem, matchedUnit: string | null) => {
            const sourceAmount = Number(line.extracted.amount ?? 0);
            const resolvedAmount = Number(line.amount ?? 0);
            const factor = sourceAmount > 0 && resolvedAmount > 0
                ? resolvedAmount / sourceAmount
                : 1;
            const amountChanged = Number.isFinite(factor) && Math.abs(factor - 1) > 0.0001;
            return {
                unit_index: line.unitIndex,
                source_unit: line.extracted.unit ?? null,
                matched_unit: matchedUnit,
                source_amount: sourceAmount,
                matched_amount: resolvedAmount,
                amount_mode: amountChanged ? 'multiply' : 'as_is',
                amount_factor: amountChanged ? factor : 1,
                source_barcode: line.extracted.barcode ?? null,
                source_ext_id: line.extracted.extId ?? null,
                conversion_ratio: product.conversion_ratio ?? 0,
                unit_preference: product.unit_preferece ?? null,
            };
        };
        const addRow = (row: MatchConfirmation) => {
            const key = [
                row.entity_type ?? entityType,
                String(row.scope_id ?? ''),
                row.req_name.trim().toLowerCase(),
                String(row.req_unit ?? '').trim().toLowerCase(),
                String(row.matched_id),
            ].join('::');
            rowsByKey.set(key, row);
        };

        if (entity) {
            const erpEntityType: MatchConfirmation['entity_type'] =
                process === 'supplier_invoice' ? 'supplier' : 'client';
            const entityAliases = [
                smartSuggested?.entityName,
                smartSuggested?.externalEntityId != null ? String(smartSuggested.externalEntityId) : null,
                entity.name,
                entity.externalId,
            ].filter((value): value is string => Boolean(value && String(value).trim()));
            entityAliases.forEach((alias) => addRow({
                entity_type: erpEntityType,
                scope_id: null,
                req_name: alias,
                req_unit: null,
                matched_id: entity.id,
                matched_name: entity.name,
                matched_unit: null,
            }));
        }

        matches
            .filter((line) => line.matchedProduct && line.extracted.productName)
            .forEach((line) => {
                const product = line.matchedProduct!;
                const reqUnit = line.extracted.unit ?? '';
                const matchUnit = product.available_units?.[line.unitIndex] ?? product.unit ?? '';
                const metadata = buildLineMemoryMetadata(line, product, matchUnit || null);
                const baseRow: Omit<MatchConfirmation, 'req_name' | 'req_unit'> = {
                    entity_type: entityType,
                    matched_id: Number(product.id),
                    matched_name: product.name,
                    matched_unit: matchUnit || null,
                    metadata,
                };
                addRow({
                    ...baseRow,
                    req_name: line.extracted.productName,
                    req_unit: reqUnit || null,
                });
                [line.extracted.barcode, line.extracted.extId]
                    .filter((value): value is string => Boolean(value && String(value).trim()))
                    .forEach((identifier) => addRow({
                        ...baseRow,
                        req_name: identifier,
                        req_unit: null,
                    }));
            });
        return Array.from(rowsByKey.values());
    };

    const saveMatchingMemory = async () => {
        if (!entity) return;
        const confirmations = buildMatchConfirmations();
        if (!confirmations.length) return;
        await aiConfirmMatches(confirmations, {
            clientId: process === 'order' ? entity.id : null,
            supplierId: process === 'supplier_invoice' ? entity.id : null,
            entityRole: process === 'supplier_invoice' ? 'supplier' : 'client',
        });
    };

    const yieldUiPaint = () =>
        new Promise<void>((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });

    const showExtractionResult = async (extractedDoc: ExtractedDocument) => {
        setExtracted(extractedDoc);
        setExtractionInputFields(snapshotExtractionInputFields(extractedDoc));
        setMatches(buildProvisionalMatchesFromExtracted(extractedDoc));
        setStage('extracted');
        await yieldUiPaint();
    };

    const handleAnalyze = async () => {
        setError(null);
        setMatches([]);
        setDebugEvents([]);
        setSmartSuggested(null);
        setExtractionInputFields({hadBarcode: false, hadExtId: false});
        try {
            // ── Smart path (server-side extract + resolve, supports image / PDF) ─────
            // Activated when (a) the user toggled smart mode OR (b) a binary file
            // was uploaded (image/PDF can't go through the legacy text path).
            const isBinaryUpload = uploadedBinary != null;
            const wantSmart = useSmartExtraction || isBinaryUpload;
            if (wantSmart) {
                if (!isBinaryUpload && !inputText.trim()) throw new Error('הכנס טקסט או העלה קובץ לפני הפעלה');
                addDebugEvent({
                    stage: 'start',
                    title: 'תחילת ניתוח (smart, צד-שרת)',
                    data: {
                        process,
                        entity,
                        date: dateObjectToString(date),
                        inputKind: isBinaryUpload ? uploadedBinary!.kind : 'text',
                        fileName:  isBinaryUpload ? uploadedBinary!.fileName : null,
                    },
                });
                setStage('extracting');
                // ⚡ Smart server mode: the server's _build_candidates_from_adapter
                // already pulls the full catalog (with this company's exact unit
                // spellings) when resolving products. So we DON'T preload the
                // pricelist on the client just to extract unit hints — that was
                // a duplicate fetch that doubled the perceived latency of every
                // smart-mode analyze. The server uses the bundled
                // COMMON_HEBREW_UNITS baseline + its own catalog for unit
                // disambiguation. If accuracy ever drops on a company with
                // unusual unit spellings we can revisit.
                const smartRes = await runSmartDocumentExtraction({
                    process,
                    entityId:   entity?.id ?? null,
                    entityName: entity?.name ?? null,
                    text: isBinaryUpload ? undefined : inputText,
                    image: uploadedBinary?.kind === 'image'
                        ? {base64: uploadedBinary.base64, mime_type: uploadedBinary.mimeType}
                        : undefined,
                    pdf:   uploadedBinary?.kind === 'pdf'
                        ? {base64: uploadedBinary.base64}
                        : undefined,
                    conversionRateBlocker,
                    selfCompany: selfCompanyHint,
                    ignoreUnavailable: ignoreUnavailableProducts,
                    // When the streaming checkbox is on, split smart pipeline
                    // into two HTTP calls and paint the line table as soon as
                    // extract returns (resolve continues in the background).
                    onExtracted: useStreamingMatch
                        ? async (partialExtracted) => {
                            addDebugEvent({
                                stage: 'extract',
                                title: 'תוצאת extract (שלב 1/2 — סטרימינג)',
                                data: partialExtracted,
                            });
                            await showExtractionResult(partialExtracted);
                            setStage('matching');
                        }
                        : undefined,
                    debug: addDebugEvent,
                });
                if (!smartRes.extracted.products.length) {
                    throw new Error('לא נמצאו שורות מוצרים במקור');
                }
                addDebugEvent({stage: 'extract', title: 'תוצאת חילוץ (שלב 1)', data: smartRes.extracted});
                await showExtractionResult(smartRes.extracted);
                setSmartSuggested(smartRes.suggested);
                addDebugEvent({stage: 'smart-result', title: 'JSON מאוחד (extracted + resolved)', data: smartRes.raw});
                setStage('matching');

                // If the user did not choose an entity, let the smart resolver
                // choose it from document header fields (name / external id /
                // tax id). Match against MULTIPLE signals — the printed id
                // ("1052") is usually a SUPPLIER-EXTERNAL number, not the ERP
                // internal id, so we also try ext_ref + name. Server-resolved
                // ids (suggested.entityId after resolution) are ERP ids — those
                // get matched by .id first.
                const findEntityBySignals = (): TextToDataEntityOption | null => {
                    const sug = smartRes.suggested;
                    const tryId = sug.entityId != null ? String(sug.entityId) : null;
                    const tryExtId = sug.externalEntityId != null ? String(sug.externalEntityId) : null;
                    const tryName = (sug.entityName ?? '').trim();
                    // 1. Server-resolved ERP id.
                    if (tryId) {
                        const byId = entityOptions.find((opt) => String(opt.id) === tryId);
                        if (byId) return byId;
                    }
                    // 2. Printed external id ("מספר לקוח" on the PDF).
                    if (tryExtId) {
                        const byExt = entityOptions.find((opt) =>
                            opt.externalId && String(opt.externalId).trim() === tryExtId);
                        if (byExt) return byExt;
                        // Also try opt.id == ext_id (some ERPs do print the internal id).
                        const byIdAsExt = entityOptions.find((opt) => String(opt.id) === tryExtId);
                        if (byIdAsExt) return byIdAsExt;
                    }
                    // 3. Exact name (case-insensitive trim).
                    if (tryName) {
                        const needle = tryName.toLowerCase();
                        const byNameExact = entityOptions.find((opt) =>
                            (opt.name ?? '').trim().toLowerCase() === needle
                            || (opt.businessName ?? '').trim().toLowerCase() === needle);
                        if (byNameExact) return byNameExact;
                        // 4. Substring fallback — e.g. "משרד הביטחון" vs "משרד הבטחון".
                        const byNamePartial = entityOptions.find((opt) => {
                            const n = (opt.name ?? '').toLowerCase();
                            const b = (opt.businessName ?? '').toLowerCase();
                            return n.includes(needle) || needle.includes(n) || b.includes(needle) || needle.includes(b);
                        });
                        if (byNamePartial) return byNamePartial;
                    }
                    return null;
                };
                const resolvedEntity = findEntityBySignals();
                const activeEntity = entity ?? resolvedEntity ?? null;
                if (!entity && resolvedEntity) {
                    setEntity(resolvedEntity);
                    addDebugEvent({
                        stage: 'smart-entity',
                        title: 'ישות זוהתה ונבחרה אוטומטית',
                        data: resolvedEntity,
                    });
                }
                if (!activeEntity) {
                    addDebugEvent({
                        stage: 'smart-entity',
                        title: 'לא זוהתה ישות ERP מהמסמך',
                        data: smartRes.suggested,
                    });
                }

                // ⚡ Smart server mode: skip the heavy `loadContextFor` —
                // it would refetch the entire pricelist + 3 previous documents
                // that the SERVER already pulled for matching. Instead, we
                // only fetch IItem details for the products that were actually
                // MATCHED (typically 5-50 rows, not 1000+). Previous documents
                // for the side panel are loaded lazily via a button (cosmetic
                // only; server already used them for matching).
                const matchedProductIds = Array.from(new Set(
                    smartRes.matches
                        .map((m) => Number(m.productId))
                        .filter((id) => Number.isFinite(id) && id > 0),
                ));
                const smartContext = activeEntity && matchedProductIds.length
                    ? {
                        products: await loadMatchedProductsOnly(activeEntity, matchedProductIds),
                        previousDocuments: [] as ExtendedDocument[],
                    }
                    : {products: [] as IItem[], previousDocuments: [] as ExtendedDocument[]};
                setContextProducts(smartContext.products);
                setPreviousDocumentsPreview([]);
                setPreviousDocsLoaded(false);
                setActiveEntityForContext(activeEntity);
                addDebugEvent({
                    stage: 'context',
                    title: 'IItem scoped fetch (smart) — דילוג על pricelist מלא + previous docs',
                    data: {
                        matchedProductIds,
                        fetched: smartContext.products.length,
                        skipped: ['full pricelist (server already pulled it)', 'previous documents (lazy in side panel)'],
                    },
                });

                // Convert the server's matches into the drawer's MatchedProductLine
                // shape using the same applyAiMatches helper. We feed an empty
                // candidates map per line and a synthetic catalog — applyAiMatches
                // pulls the matchedProduct by productId from contextProducts when
                // available, otherwise falls back to the candidate provided here.
                //
                // For smart mode we don't have a local catalog snapshot; build a
                // tiny one-element candidate list per resolved line so the table
                // can render product names + units even without contextProducts.
                const candidatesByLine: Record<string, ProductMatchCandidate[]> = {};
                smartRes.matches.forEach((m) => {
                    if (m.productId == null) {
                        candidatesByLine[String(m.lineId)] = [];
                        return;
                    }
                    const raw = smartRes.raw.products.find((p) => String(p.lineId) === String(m.lineId));
                    // Try the most reliable name source first: server's match
                    // (productName), then the cached catalog by productId, then the
                    // raw matchProductName field, then finally the extracted source
                    // text. This guarantees the table renders SOMETHING even when
                    // the server doesn't pre-populate matchProductName.
                    // setContextProducts above is async — use the just-loaded
                    // smartContext.products directly here.
                    const fromCatalog = smartContext.products.find((p) => Number(p.id) === Number(m.productId));
                    const fallbackName = (m as any).productName
                        ?? fromCatalog?.name
                        ?? raw?.matchProductName
                        ?? raw?.productName
                        ?? `מוצר ${m.productId}`;
                    const resolvedUnit = m.unit ?? raw?.unit ?? undefined;
                    candidatesByLine[String(m.lineId)] = [{
                        productId:      Number(m.productId),
                        name:           String(fallbackName),
                        score:          m.confidence ?? 0.9,
                        unit:           resolvedUnit ?? undefined,
                        availableUnits: resolvedUnit ? [resolvedUnit] : [],
                        priority:       0,
                        prevOrdered:    0,
                    }];
                });
                const appliedAiMatches = applyAiMatches(
                    smartRes.extracted.products,
                    smartContext.products,
                    candidatesByLine,
                    smartRes.matches,
                    summarizePreviousDocumentProducts(smartContext.previousDocuments as any[]),
                    conversionRateBlocker,
                );
                const appliedByLine = new Map(appliedAiMatches.map((line) => [line.lineId, line]));
                const appliedMatches = smartRes.extracted.products
                    .map((line) => appliedByLine.get(line.lineId))
                    .filter(Boolean) as MatchedProductLine[];
                setMatches(appliedMatches);
                setStage('ready');
                return;
            }

            // ── Streaming path (single NDJSON call, server does extract + match) ──
            // The server flushes an `extracted` event after the first LLM call
            // so the line table lights up immediately while the matching LLM
            // is still running. Net perceived latency: roughly halved.
            const wantStream = useStreamingMatch && !isBinaryUpload;
            if (wantStream) {
                if (!entity) throw new Error('בחר לקוח או ספק לפני הפעלה');
                if (!inputText.trim()) throw new Error('הכנס טקסט לפני הפעלה');
                addDebugEvent({
                    stage: 'start',
                    title: 'תחילת ניתוח (streaming, שרת)',
                    data: {process, entity, date: dateObjectToString(date), inputText},
                });

                setStage('loading-context');
                // Load context in parallel — the streaming server doesn't return
                // the catalog/previous-documents lists; we still need them for
                // the side panel preview and applyAiMatches.
                const contextPromise = loadContext().catch((err) => {
                    addDebugEvent({stage: 'context', title: 'שגיאה בטעינת קונטקסט (ממשיך)', data: String(err)});
                    return {products: [] as IItem[], previousDocuments: [] as ExtendedDocument[]};
                });

                setStage('extracting');
                // Server's ExtractedDocument has `confidence: number | null` while
                // the local one is `confidence?: number` — same shape modulo nulls.
                // Normalise once at the boundary so downstream code stays clean.
                const normaliseExtracted = (raw: any): ExtractedDocument => ({
                    date:        raw?.date        ?? null,
                    entityName:  raw?.entityName  ?? null,
                    entityId:    raw?.entityId    ?? null,
                    total:       raw?.total       ?? null,
                    products: (raw?.products ?? []).map((p: any) => ({
                        lineId:      String(p.lineId ?? ''),
                        productName: String(p.productName ?? ''),
                        amount:      Number(p.amount ?? 0),
                        unit:        p.unit ?? null,
                        price:       p.price ?? null,
                        total:       p.total ?? null,
                        barcode:     p.barcode ?? null,
                        extId:       p.extId   ?? null,
                        confidence:  p.confidence == null ? undefined : Number(p.confidence),
                        rawText:     p.rawText ?? undefined,
                    })),
                });

                let extractedFromStream: ExtractedDocument | null = null;
                const isSupplier = process === 'supplier_invoice';
                const streamCaller = isSupplier ? aiMatchSupplierProductsStream : aiMatchProductsStream;
                const result = await streamCaller(
                    {
                        process,
                        client_id:   !isSupplier ? (entity?.id ?? null) : null,
                        supplier_id: isSupplier  ? (entity?.id ?? null) : null,
                        text: inputText,
                        options: {
                            use_memory:              true,
                            conversion_rate_blocker: conversionRateBlocker,
                        },
                        feature: isSupplier ? 'match_supplier_products_stream' : 'match_products_stream',
                    } as any,
                    {
                        onExtracted: (evt) => {
                            const norm = normaliseExtracted(evt.extracted);
                            extractedFromStream = norm;
                            addDebugEvent({stage: 'extract', title: 'extracted (שלב 1/2 מהשרת)', data: norm});
                            // Light up the UI immediately — extract is done, matches still computing
                            void showExtractionResult(norm);
                            setStage('matching');
                        },
                        onMatches: (evt) => {
                            addDebugEvent({stage: 'matches', title: 'matches (שלב 2/2 מהשרת)', data: evt});
                        },
                        onError: (evt) => {
                            addDebugEvent({stage: 'error', title: 'שגיאה בסטרים', data: evt});
                        },
                    },
                );

                if (!result || result.event === 'error') {
                    throw new Error((result as any)?.detail || 'שגיאה לא ידועה בסטרים');
                }
                const matchesEvt = result as SmartMatchMatchesEvent;
                const finalExtracted = matchesEvt.extracted
                    ? normaliseExtracted(matchesEvt.extracted)
                    : extractedFromStream;
                if (!finalExtracted) throw new Error('extract חסר בתגובת השרת');

                const streamContext = await contextPromise;
                setContextProducts(streamContext.products);
                setPreviousDocumentsPreview((streamContext.previousDocuments ?? []).slice(0, 3));

                // Build a synthetic 1-element candidate list per matched line so
                // applyAiMatches can hydrate the table (it pulls IItem details
                // out of contextProducts by id, falling back to this stub for
                // products that aren't in the cached catalog yet).
                const candidatesByLine: Record<string, ProductMatchCandidate[]> = {};
                (matchesEvt.matches ?? []).forEach((m) => {
                    if (m.productId != null) {
                        candidatesByLine[String(m.lineId)] = [{
                            productId:      Number(m.productId),
                            name:           m.productName ?? '',
                            score:          m.confidence ?? 0.9,
                            unit:           m.unit ?? undefined,
                            availableUnits: m.unit ? [m.unit] : [],
                            priority:       0,
                            prevOrdered:    0,
                        }];
                    } else {
                        candidatesByLine[String(m.lineId)] = [];
                    }
                });
                const streamPrevProducts = summarizePreviousDocumentProducts(streamContext.previousDocuments as any[]);
                const appliedStreamMatches = applyAiMatches(
                    finalExtracted.products,
                    streamContext.products,
                    candidatesByLine,
                    (matchesEvt.matches ?? []) as any,
                    streamPrevProducts,
                    conversionRateBlocker,
                );
                const appliedStreamByLine = new Map(appliedStreamMatches.map((line) => [line.lineId, line]));
                const appliedFinal = finalExtracted.products
                    .map((line) => appliedStreamByLine.get(line.lineId))
                    .filter(Boolean) as MatchedProductLine[];
                setMatches(appliedFinal);
                setStage('ready');
                return;
            }

            if (!inputText.trim()) throw new Error('הכנס טקסט או העלה קובץ לפני הפעלה');
            addDebugEvent({
                stage: 'start',
                title: 'תחילת ניתוח',
                data: {
                    process,
                    entity,
                    date: dateObjectToString(date),
                    conversionRateBlocker,
                    inputText,
                    textToDataMatchBackend: TEXT_TO_DATA_MATCH_BACKEND,
                },
            });
            setStage('loading-context');
            const context = await loadContext();
            setContextProducts(context.products);
            setPreviousDocumentsPreview((context.previousDocuments ?? []).slice(0, 3));

            // Detect a document already created for THIS entity on THIS exact date,
            // so the user can pick "create new" vs "merge into existing" before saving.
            const wantedSameDayType = process === 'order' ? 'order' : 'sup_invoice';
            const dayString = dateObjectToString(date);
            const sameDayMatch = (context.previousDocuments ?? []).find((doc: any) => {
                if (!doc?.date) return false;
                if (doc?.type !== wantedSameDayType) return false;
                const docDate = doc.date instanceof Date ? doc.date : new Date(doc.date);
                if (!docDate || Number.isNaN(docDate.getTime())) return false;
                return dateObjectToString(docDate) === dayString;
            }) ?? null;
            setSameDayDocument(sameDayMatch as ExtendedDocument | null);
            addDebugEvent({
                stage: 'context',
                title: 'בדיקת מסמך באותו יום ללקוח/ספק',
                data: sameDayMatch
                    ? {found: true, id: (sameDayMatch as any).id, type: (sameDayMatch as any).type, date: (sameDayMatch as any).date, productsCount: (sameDayMatch as any)?.products?.length ?? 0}
                    : {found: false, day: dayString},
            });
            const previousProducts = summarizePreviousDocumentProducts(context.previousDocuments as any[]);
            addDebugEvent({
                stage: 'context',
                title: 'קונטקסט מהמערכת',
                data: {
                    productsCount: context.products.length,
                    products: context.products.map(summarizeProductForDebug),
                    previousDocumentsCount: context.previousDocuments.length,
                    matchingMemory: 'ai_assistent ai_match_memory (via product-matching)',
                    previousDocuments: context.previousDocuments.map((doc: any) => ({
                        id: doc.id,
                        type: doc.type,
                        date: doc.date,
                        products: (doc.products ?? []).map(summarizeProductForDebug),
                    })),
                    previousProductsForAi: previousProducts,
                },
            });

            setStage('extracting');
            const extractedDoc = await extractDocumentFromText({
                text: inputText,
                process,
                knownUnits: knownUnitsFromProducts(context.products),
                debug: addDebugEvent,
            });
            if (!extractedDoc.products.length) throw new Error('לא נמצאו שורות מוצרים בטקסט');
            addDebugEvent({stage: 'extract', title: 'תוצאת חילוץ מנורמלת (שלב 1)', data: extractedDoc});
            await showExtractionResult(extractedDoc);

            const candidatesByLine: Record<string, ProductMatchCandidate[]> = {};
            extractedDoc.products.forEach((line) => {
                candidatesByLine[line.lineId] = buildProductCandidates(
                    line,
                    context.products,
                    4,
                    previousProducts,
                    ignoreUnavailableProducts,
                );
            });
            addDebugEvent({
                stage: 'candidates',
                title: ignoreUnavailableProducts
                    ? 'מועמדים (ללא מוצרים לא זמינים במחירון)'
                    : 'מועמדים מאוחדים: 4 fuzzy + priority + הזמנות קודמות',
                data: {ignoreUnavailableProducts, candidatesByLine},
            });

            setStage('matching');
            addDebugEvent({
                stage: 'history',
                title: '3 מסמכים קודמים מסוכמים ל-AI (כולל usedUnit)',
                data: previousProducts,
            });
            const aiMatches = await matchExtractedProducts({
                process,
                extracted: extractedDoc,
                candidatesByLine,
                clientId: process === 'order' ? entity!.id : null,
                supplierId: process === 'supplier_invoice' ? entity!.id : null,
                previousProducts,
                conversionRateBlocker,
                debug: addDebugEvent,
            });
            const appliedAiMatches = applyAiMatches(
                extractedDoc.products,
                context.products,
                candidatesByLine,
                aiMatches,
                previousProducts,
                conversionRateBlocker,
            );
            const appliedByLine = new Map(appliedAiMatches.map((line) => [line.lineId, line]));
            const appliedMatches = extractedDoc.products
                .map((line) => appliedByLine.get(line.lineId))
                .filter(Boolean) as MatchedProductLine[];
            addDebugEvent({stage: 'result', title: 'תוצאה סופית אחרי מיפוי מקומי', data: appliedMatches.map((line) => ({
                ...line,
                matchedProduct: line.matchedProduct ? summarizeProductForDebug(line.matchedProduct) : undefined,
            }))});
            setMatches(appliedMatches);
            setStage('ready');
        } catch (err) {
            addDebugEvent({stage: 'error', title: 'שגיאה', data: err instanceof Error ? {message: err.message, stack: err.stack} : err});
            setError(err instanceof Error ? err.message : String(err));
            setStage('idle');
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setError(null);
        setUploadedBinary(null);
        setUploadedTextFileInfo(null);
        try {
            const kind = classifyFile(file);
            if (kind === 'image' || kind === 'pdf') {
                // Binary upload — keep it as base64 and force the smart pipeline
                // (server-side extract+resolve handles vision + PDF rasterization).
                const base64 = await readFileAsBase64(file);
                setUploadedBinary({
                    kind,
                    fileName: file.name,
                    base64,
                    mimeType: file.type || (kind === 'pdf' ? 'application/pdf' : 'image/jpeg'),
                });
                setInputText(kind === 'pdf' ? `[PDF: ${file.name}]` : `[Image: ${file.name}]`);
                setUseSmartExtraction(true);
            } else {
                // Plain text/csv/tsv — keep the existing text path.
                const text = await readFileAsText(file);
                setInputText(text);
                const lineCount = text.split(/\r?\n/).filter((l) => l.trim() !== '').length;
                setUploadedTextFileInfo({fileName: file.name, lineCount});
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            event.target.value = '';
        }
    };

    const clearUploadedBinary = React.useCallback(() => {
        setUploadedBinary(null);
        setInputText('');
        setSmartSuggested(null);
        setUploadedTextFileInfo(null);
    }, []);

    const handleCreate = async (asUpdate: boolean = false) => {
        if (!entity || !canCreate) return;
        setError(null);
        setStage('creating');
        try {
            const newProducts = matchedLinesToItems(matches, {includeSourceNotes: saveSourceAsNotes});

            // === UPDATE existing same-day document (merge new lines into it) ===
            if (asUpdate && sameDayDocument && (process === 'order' || process === 'supplier_invoice')) {
                const existingProducts = (((sameDayDocument as any)?.products ?? []) as IItem[]).map((p, idx) => ({
                    ...p,
                    notePlacement: p.notePlacement ?? idx,
                }));
                const mergedProducts = [...existingProducts, ...newProducts.map((p, idx) => ({
                    ...p,
                    notePlacement: existingProducts.length + idx,
                }))];
                const totals = calcDocumentTotals(mergedProducts, extracted ?? undefined);
                const existingComment = ((sameDayDocument as any)?.comment ?? '').toString();
                const updateComment = existingComment
                    ? `${existingComment} | עודכן מ-TextToDataDrawerAi`
                    : 'עודכן מ-TextToDataDrawerAi';

                if (process === 'order') {
                    const document = createDocument({
                        id: Number((sameDayDocument as any).id) || 0,
                        entId: entity.id,
                        entName: entity.name,
                        entBName: entity.businessName,
                        date,
                        issueDate: new Date(),
                        type: 'order',
                        status: (sameDayDocument as any)?.status ?? 'open',
                        products: mergedProducts,
                        comment: updateComment,
                        mainValue: totals.mainValue,
                        preValue: totals.preValue,
                        taxValue: totals.taxValue,
                    });
                    const updated = await updateClientDocument.mutateAsync(document);
                    await saveMatchingMemory().catch((memoryError) => {
                        addDebugEvent({
                            stage: 'memory',
                            title: 'שגיאה בשמירת זיכרון התאמות',
                            data: memoryError instanceof Error ? memoryError.message : memoryError,
                        });
                    });
                    onDocumentCreated?.(updated);
                    setStage('ready');
                    onClose();
                    return;
                }

                const document = createDocument({
                    id: Number((sameDayDocument as any).id) || 0,
                    entId: entity.id,
                    entName: entity.name,
                    entBName: entity.businessName,
                    date,
                    issueDate: new Date(),
                    type: 'sup_invoice',
                    status: (sameDayDocument as any)?.status ?? 'open',
                    products: mergedProducts,
                    payments: (sameDayDocument as any)?.payments ?? [],
                    deliveryCertificatesIds: (sameDayDocument as any)?.deliveryCertificatesIds ?? [],
                    discount: (sameDayDocument as any)?.discount ?? 0,
                    details: (sameDayDocument as any)?.details ?? createDetailsFromEntity(entity),
                    comment: updateComment,
                    mainValue: totals.mainValue,
                    preValue: totals.preValue,
                    taxValue: totals.taxValue,
                    hand: (sameDayDocument as any)?.hand ?? 0,
                    hand_tax: (sameDayDocument as any)?.hand_tax ?? 0,
                    round: (sameDayDocument as any)?.round ?? 0,
                    round_tax: (sameDayDocument as any)?.round_tax ?? 0,
                    handArray: (sameDayDocument as any)?.handArray ?? [],
                    supplierInvoiceNumber: (sameDayDocument as any)?.supplierInvoiceNumber,
                });
                (document as any).lines_edit = 1;
                (document as any).date_edit = 1;
                const updated = await updateSupplierDocument.mutateAsync(document);
                await saveMatchingMemory().catch((memoryError) => {
                    addDebugEvent({
                        stage: 'memory',
                        title: 'שגיאה בשמירת זיכרון התאמות',
                        data: memoryError instanceof Error ? memoryError.message : memoryError,
                    });
                });
                onDocumentCreated?.(updated);
                setStage('ready');
                onClose();
                return;
            }

            // === CREATE new document ===
            const totals = calcDocumentTotals(newProducts, extracted ?? undefined);

            if (process === 'order') {
                const document = createDocument({
                    id: 0,
                    entId: entity.id,
                    entName: entity.name,
                    entBName: entity.businessName,
                    date,
                    issueDate: new Date(),
                    type: 'order',
                    status: 'open',
                    products: newProducts,
                    comment: 'נוצר מ-TextToDataDrawerAi',
                    mainValue: totals.mainValue,
                    preValue: totals.preValue,
                    taxValue: totals.taxValue,
                });
                const created = await createOrderDocument.mutateAsync(document);
                await saveMatchingMemory().catch((memoryError) => {
                    addDebugEvent({
                        stage: 'memory',
                        title: 'שגיאה בשמירת זיכרון התאמות',
                        data: memoryError instanceof Error ? memoryError.message : memoryError,
                    });
                });
                onDocumentCreated?.(created);
                setStage('ready');
                onClose();
                return;
            }

            const document = createDocument({
                id: 0,
                entId: entity.id,
                entName: entity.name,
                entBName: entity.businessName,
                date,
                issueDate: new Date(),
                type: 'sup_invoice',
                status: 'open',
                products: newProducts,
                payments: [],
                deliveryCertificatesIds: [],
                discount: 0,
                details: createDetailsFromEntity(entity),
                comment: 'נוצר מ-TextToDataDrawerAi',
                mainValue: totals.mainValue,
                preValue: totals.preValue,
                taxValue: totals.taxValue,
                hand: 0,
                hand_tax: 0,
                round: 0,
                round_tax: 0,
                handArray: [],
            });
            const created = await createSupplierInvoiceDocument.mutateAsync(document);
            await saveMatchingMemory().catch((memoryError) => {
                addDebugEvent({
                    stage: 'memory',
                    title: 'שגיאה בשמירת זיכרון התאמות',
                    data: memoryError instanceof Error ? memoryError.message : memoryError,
                });
            });
            onDocumentCreated?.(created);
            setStage('ready');
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setStage('ready');
        }
    };

    const proceedAfterNotesDecision = () => {
        if (sameDayDocument) {
            setUpdatePromptOpen(true);
        } else {
            handleCreate(false);
        }
    };

    const handlePrimaryButtonClick = () => {
        if (!notesPrefSet) {
            setNotesPrefDialogOpen(true);
            return;
        }
        proceedAfterNotesDecision();
    };

    const stageLabel = {
        idle: '',
        extracting: 'שלב 1/2 — מחלץ נתונים מהקלט...',
        'loading-context': 'טוען מוצרים, מחירים והיסטוריה...',
        extracted: 'שלב 1 הושלם — מציג שורות שחולצו',
        matching: 'שלב 2/2 — מתאים מוצרים בקטלוג...',
        ready: 'מוכן לבדיקה',
        creating: 'יוצר מסמך...',
    }[stage];

    const showMatchesTable = matches.length > 0;
    const isMatchingProducts = stage === 'matching';

    const entityNameMismatch = Boolean(
        extracted?.entityName && entity
        && !normalizeForCompare(extracted.entityName).includes(normalizeForCompare(entity.name))
        && !normalizeForCompare(entity.name).includes(normalizeForCompare(extracted.entityName))
    );

    // Whole catalog mapped to the same shape as the per-line candidates so the user can override
    // the AI's pick with ANY product (not only the shortlisted candidates) when the match is wrong.
    // Sorted by Hebrew name so the "all products" group is browsable.
    const allProductsAsCandidates = React.useMemo<ProductMatchCandidate[]>(
        () => sortCandidatesCatalogAvailableFirst(
            contextProducts.map((product) => ({
                productId: Number(product.id),
                name: product.name,
                unit: product.unit,
                availableUnits: product.available_units,
                catalogAvailable: isCatalogProductAvailable(product),
                price: product.totalPrice,
                barcode: product.barcode,
                externalId: product.external_id,
                conversionRatio: product.conversion_ratio,
                unitPreference: product.unit_preferece,
                priority: Number(product.priority ?? 0),
                prevOrdered: 0 as 0 | 1,
                score: 0,
            })),
        ),
        [contextProducts],
    );

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={isBusy ? undefined : onClose}
            sx={{'& .MuiDrawer-paper': {width: {xs: '100vw', lg: DRAWER_WIDTH}, maxWidth: '98vw', direction: 'rtl', boxSizing: 'border-box'}}}
        >
            <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                {/* === Header === */}
                <Box sx={{p: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0}}>
                    <AutoFixHighIcon/>
                    <Box sx={{flex: 1}}>
                        <Typography variant="h4" sx={{lineHeight: 1.1}}>TextToDataDrawerAi</Typography>
                        <Typography variant="caption">קליטת טקסט/קובץ להזמנה או חשבונית ספק</Typography>
                    </Box>
                    <IconButton onClick={onClose} disabled={isBusy} sx={{color: 'inherit'}}>
                        <CloseIcon/>
                    </IconButton>
                </Box>

                {/* === Top control bar (process + entity + date) === */}
                <Box sx={{px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0}}>
                    <Stack direction={{xs: 'column', md: 'row'}} spacing={1} alignItems={{xs: 'stretch', md: 'center'}}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={process}
                            onChange={(_, value) => {
                                if (!value || value === process) return;
                                setProcess(value);
                                resetAfterProcessChange();
                            }}
                            disabled={isBusy}
                            sx={{flexShrink: 0}}
                        >
                            <ToggleButton value="order">{processLabels.order}</ToggleButton>
                            <ToggleButton value="supplier_invoice">{processLabels.supplier_invoice}</ToggleButton>
                        </ToggleButtonGroup>
                        <Autocomplete
                            sx={{flex: 1, minWidth: 240}}
                            size="small"
                            options={entityOptions}
                            value={entity}
                            onChange={(_, value) => setEntity(value)}
                            getOptionLabel={(option) => `${option.name}${option.businessName ? ` - ${option.businessName}` : ''}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField {...params} label={process === 'order' ? 'לקוח' : 'ספק'} size="small"/>
                            )}
                            disabled={isBusy}
                        />
                        <TextField
                            sx={{width: {xs: '100%', md: 160}}}
                            size="small"
                            type="date"
                            label="תאריך"
                            value={dateObjectToString(date)}
                            onChange={(event) => setDate(new Date(event.target.value))}
                            InputLabelProps={{shrink: true}}
                            disabled={isBusy}
                        />
                    </Stack>
                </Box>

                {/* === Main 2-column body: sticky LEFT (input) + scrollable RIGHT (matches) === */}
                <Box sx={{flex: 1, display: 'flex', flexDirection: {xs: 'column', md: 'row'}, overflow: 'hidden', minHeight: 0}}>
                    {/* LEFT panel — original text always visible */}
                    <Paper
                        elevation={0}
                        square
                        sx={{
                            width: {xs: '100%', md: LEFT_PANEL_WIDTH},
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            p: 1.5,
                            borderLeft: {md: 1},
                            borderBottom: {xs: 1, md: 0},
                            borderColor: 'divider',
                            bgcolor: 'background.default',
                            minHeight: 0,
                            maxHeight: {md: '100%'},
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 1, flexShrink: 0}}>
                            <Tooltip title="קובץ טקסט/CSV: שורה אחת לכל מוצר, לדוגמה 'עגבניה 5 קרטון'. אפשר גם להעלות PDF או תמונה של הזמנה/חשבונית — במקרה זה החילוץ החכם יופעל אוטומטית.">
                                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon/>} disabled={isBusy}>
                                    העלה קובץ
                                    <input
                                        hidden type="file"
                                        accept=".txt,.csv,.json,.pdf,text/*,application/pdf,image/*"
                                        onChange={handleFileChange}
                                    />
                                </Button>
                            </Tooltip>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleAnalyze}
                                disabled={isBusy || (!entity && !useSmartExtraction && !uploadedBinary) || (!inputText.trim() && !uploadedBinary)}
                            >
                                נתח והתאם מוצרים
                            </Button>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={useSmartExtraction}
                                        disabled={isBusy || uploadedBinary != null}
                                        onChange={(_, checked) => setUseSmartExtraction(checked)}
                                    />
                                }
                                label={
                                    <Tooltip title="המוח של ai_assistent מבצע extract+resolve בצד-השרת (תמיכה בתמונה ו-PDF, מועמדים נבנים בשרת מתוך הקטלוג).">
                                        <Typography variant="caption">חילוץ חכם (שרת)</Typography>
                                    </Tooltip>
                                }
                                sx={{m: 0}}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={useStreamingMatch}
                                        // Active only for text-only inputs without smart extraction.
                                        // Mutually-exclusive rules live in handleAnalyze (smart > stream > legacy),
                                        // so we only disable while a job is running.
                                        disabled={isBusy}
                                        onChange={(_, checked) => setUseStreamingMatch(checked)}
                                    />
                                }
                                label={
                                    <Tooltip title="הזרמת תוצאות מהשרת: רגע אחרי שה־AI מחלץ את השורות הטבלה מתעדכנת מיד, ובינתיים ה־AI ממשיך להתאים מוצרים. (מתעלם אוטומטית כשמסומן 'חילוץ חכם' או כשעולה PDF/תמונה — במצבים אלה ה-smart path פעיל).">
                                        <Typography variant="caption">סטרימינג (תוצאות תוך כדי)</Typography>
                                    </Tooltip>
                                }
                                sx={{m: 0}}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<BugReportIcon/>}
                                onClick={() => setDebugOpen(true)}
                                disabled={debugEvents.length === 0}
                            >
                                דיבאג
                            </Button>
                            {isBusy ? <CircularProgress size={20}/> : null}
                            {stageLabel ? <Typography variant="caption" color="text.secondary">{stageLabel}</Typography> : null}
                        </Stack>

                        {uploadedBinary ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    icon={<UploadFileIcon fontSize="small"/>}
                                    label={`${uploadedBinary.kind === 'pdf' ? 'PDF' : 'תמונה'}: ${uploadedBinary.fileName}`}
                                    onDelete={isBusy ? undefined : clearUploadedBinary}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    קלט בינארי — חילוץ חכם ינותב אוטומטית לשרת
                                </Typography>
                            </Stack>
                        ) : null}

                        {uploadedTextFileInfo ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
                                <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    icon={<UploadFileIcon fontSize="small"/>}
                                    label={`${uploadedTextFileInfo.fileName} — ${uploadedTextFileInfo.lineCount} שורות`}
                                    onDelete={isBusy ? undefined : clearUploadedBinary}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    נטען לתיבת הטקסט למטה — ניתן לערוך לפני הניתוח
                                </Typography>
                            </Stack>
                        ) : null}

                        <TextField
                            label="טקסט הזמנה / חשבונית"
                            multiline
                            value={inputText}
                            onChange={(event) => {
                                setInputText(event.target.value);
                                // Text was hand-edited after the upload, so the line count preflight is stale now.
                                setUploadedTextFileInfo(null);
                            }}
                            placeholder="לדוגמה: עגבניה 5 קרטון, מלפפון 12 קג..."
                            disabled={isBusy}
                            sx={{
                                flex: 1,
                                minHeight: 220,
                                display: 'flex',
                                '& .MuiInputBase-root': {
                                    flex: 1,
                                    alignItems: 'flex-start',
                                    overflow: 'hidden',
                                    fontSize: 14,
                                    lineHeight: 1.4,
                                },
                                '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
                                    height: '100% !important',
                                    overflow: 'auto !important',
                                },
                            }}
                        />

                        {/* Side info — alerts + previous docs. Has its own scroll if long. */}
                        {(error || extracted || sameDayDocument || entityNameMismatch || previousDocumentsPreview.length > 0 || (activeEntityForContext && stage === 'ready')) ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0, maxHeight: '40%', overflowY: 'auto'}}>
                                {error ? <Alert severity="error">{error}</Alert> : null}

                                {extracted ? (
                                    <Alert severity="info" sx={{py: 0.25}}>
                                        נמצאו {extracted.products.length} שורות{extracted.total ? `, סה״כ ${extracted.total}` : ''}
                                        {extracted.entityName ? `, שם במסמך: ${extracted.entityName}` : ''}
                                    </Alert>
                                ) : null}

                                {smartSuggested && (
                                    smartSuggested.docId ||
                                    smartSuggested.allocation ||
                                    smartSuggested.date ||
                                    smartSuggested.canonicalDocType
                                ) ? (
                                    <Alert severity="success" sx={{py: 0.25}}>
                                        <Typography variant="caption" sx={{display: 'block', fontWeight: 600}}>
                                            פרטים שזוהו אוטומטית מהמסמך:
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} sx={{flexWrap: 'wrap', mt: 0.25}}>
                                            {smartSuggested.canonicalDocType ? (
                                                <Chip size="small" variant="outlined" label={`סוג: ${smartSuggested.canonicalDocType}`}/>
                                            ) : null}
                                            {smartSuggested.docId ? (
                                                <Chip size="small" variant="outlined" label={`מס׳ מסמך: ${smartSuggested.docId}`}/>
                                            ) : null}
                                            {smartSuggested.allocation ? (
                                                <Chip size="small" variant="outlined" color="primary" label={`מס׳ הקצאה: ${smartSuggested.allocation}`}/>
                                            ) : null}
                                            {smartSuggested.date ? (
                                                <Chip size="small" variant="outlined" label={`תאריך: ${smartSuggested.date}`}/>
                                            ) : null}
                                            {smartSuggested.entityName && smartSuggested.entityId == null ? (
                                                <Chip size="small" variant="outlined" color="warning" label={`שם במסמך: ${smartSuggested.entityName} (לא הותאם ב-ERP)`}/>
                                            ) : null}
                                            {smartSuggested.externalEntityId ? (
                                                <Chip size="small" variant="outlined" label={`מזהה חיצוני: ${smartSuggested.externalEntityId}`}/>
                                            ) : null}
                                        </Stack>
                                    </Alert>
                                ) : null}

                                {sameDayDocument ? (
                                    <Alert severity="warning" sx={{py: 0.25}}>
                                        כבר קיים {process === 'order' ? 'הזמנה' : 'מסמך'} ל{process === 'order' ? 'לקוח' : 'ספק'} זה בתאריך {dateObjectToString(date)}
                                        {` (מסמך #${(sameDayDocument as any)?.id ?? '—'}, ${((sameDayDocument as any)?.products?.length ?? 0)} שורות)`}.
                                        בלחיצה על שמירה תופיע בקשה ליצור חדש או לעדכן את הקיים.
                                    </Alert>
                                ) : null}

                                {entityNameMismatch ? (
                                    <Alert severity="warning" sx={{py: 0.25}}>
                                        שים לב: שם הישות בטקסט שחולץ ("{extracted!.entityName}") שונה מהבחירה במסך ("{entity!.name}"). ודא שהמסמך אכן שייך לישות שבחרת.
                                    </Alert>
                                ) : null}

                                {previousDocumentsPreview.length > 0 ? (
                                    <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5}}>
                                        <Typography variant="caption" sx={{fontWeight: 600}}>מסמכים קודמים:</Typography>
                                        {previousDocumentsPreview.map((document, index) => (
                                            <Chip
                                                key={`${document.type}_${document.id}_${index}`}
                                                size="small"
                                                clickable
                                                color="info"
                                                variant="outlined"
                                                label={`${index + 1}. ${document.name ?? document.type}${document.id ? ` #${document.id}` : ''}${document.date ? ` ${dateObjectToString(document.date)}` : ''}`}
                                                onClick={() => onDocumentCreated?.(document)}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    // Smart-mode lazy load: the analyze flow no longer pre-fetches
                                    // previous documents (the server already used them for matching).
                                    // Surface a single-click button so the user can still browse the
                                    // last orders without paying the fetch cost on every analyze.
                                    activeEntityForContext && stage === 'ready' && !previousDocsLoaded ? (
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                            <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                                מסמכים קודמים לא נטענו (חיסכון בזמן בניתוח)
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="info"
                                                sx={{py: 0.1, px: 1, minHeight: 22, fontSize: '0.72rem'}}
                                                disabled={previousDocsLoading}
                                                onClick={() => loadPreviousDocumentsForSidePanel(activeEntityForContext)}
                                            >
                                                {previousDocsLoading ? (
                                                    <CircularProgress size={12} sx={{mr: 0.5}}/>
                                                ) : null}
                                                טען מסמכים אחרונים
                                            </Button>
                                        </Box>
                                    ) : null
                                )}
                            </Box>
                        ) : null}
                    </Paper>

                    {/* RIGHT panel — matches table + manual add (scrollable) */}
                    <Box sx={{flex: 1, p: 1.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0}}>
                        {!showMatchesTable ? (
                            <Box sx={{
                                flex: 1,
                                minHeight: 200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 1,
                                borderStyle: 'dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                color: 'text.secondary',
                                p: 4,
                                textAlign: 'center',
                            }}>
                                <Typography variant="body2">
                                    {stage === 'extracting' || stage === 'loading-context'
                                        ? 'מנתח את הקלט...'
                                        : 'הזן טקסט בצד ולחץ על "נתח והתאם מוצרים" כדי לראות התאמות כאן.'}
                                </Typography>
                            </Box>
                        ) : null}

                        {showMatchesTable ? (
                            <TableContainer sx={{border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto', direction: 'rtl'}}>
                                <Table
                                    stickyHeader
                                    size="small"
                                    sx={{
                                        minWidth: 1060,
                                        direction: 'rtl',
                                        '& .MuiTableCell-root': {textAlign: 'right', py: 0.25, px: 0.75, verticalAlign: 'middle'},
                                        '& .MuiTableHead-root .MuiTableCell-root': {py: 0.5, fontWeight: 600, fontSize: 12, lineHeight: 1.2},
                                        '& .MuiTextField-root, & .MuiFormControl-root': {m: 0},
                                        '& .MuiInputBase-input': {fontSize: 13},
                                        '& .MuiInputLabel-root': {fontSize: 12},
                                        '& .MuiAutocomplete-inputRoot': {py: '2px !important', minHeight: 36},
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="right" sx={{width: 44}}>מיין</TableCell>
                                            <TableCell align="right" sx={{width: 260}}>מקור / מזהים</TableCell>
                                            <TableCell align="right" sx={{width: 360}}>מוצר מותאם</TableCell>
                                            <TableCell align="right" sx={{width: 90}}>כמות</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>יחידה</TableCell>
                                            <TableCell align="right" sx={{width: 100}}>מחיר</TableCell>
                                            <TableCell align="right" sx={{width: 150}}>בטחון / פעולות</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {matches.map((line, index) => {
                                            const candidateIdSet = new Set(line.candidates.map((candidate) => candidate.productId));
                                            const lineOptions: ProductMatchCandidate[] = [
                                                ...sortCandidatesCatalogAvailableFirst(line.candidates),
                                                ...allProductsAsCandidates.filter((candidate) => !candidateIdSet.has(candidate.productId)),
                                            ];
                                            const matchedUnavailable = Boolean(
                                                line.matchedProduct && !isCatalogProductAvailable(line.matchedProduct),
                                            );
                                            return (
                                            <TableRow
                                                key={line.lineId}
                                                sx={{
                                                    ...(matchedUnavailable
                                                        ? {bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14)}
                                                        : {}),
                                                    ...(!matchedUnavailable && line.confidence < LOW_CONFIDENCE && !line.confirmed
                                                        ? {bgcolor: 'warning.light'}
                                                        : {}),
                                                }}
                                            >
                                                <TableCell align="right" sx={{width: 44}}>
                                                    <Chip size="small" label={index + 1} sx={{height: 20, fontSize: 11}}/>
                                                </TableCell>
                                                <TableCell align="right" sx={{width: 260}}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{gap: 0.5}}>
                                                        <Typography variant="body2" sx={{fontWeight: 600, lineHeight: 1.25}}>{line.extracted.productName}</Typography>
                                                        {matchedUnavailable ? (
                                                            <Chip size="small" color="warning" label="מוצר לא זמין" sx={{height: 20, fontSize: 10}}/>
                                                        ) : null}
                                                    </Stack>
                                                    {(line.extracted.unit || line.extracted.price) ? (
                                                        <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.1, display: 'block'}}>
                                                            {line.extracted.unit || ''}{line.extracted.price ? ` | ${line.extracted.price}` : ''}
                                                        </Typography>
                                                    ) : null}
                                                    {(extractionInputFields.hadBarcode || extractionInputFields.hadExtId) ? (
                                                        <Stack direction="row" spacing={0.5} sx={{mt: 0.5}}>
                                                            {extractionInputFields.hadBarcode ? (
                                                                <TextField
                                                                    size="small"
                                                                    label="ברקוד"
                                                                    value={line.extracted.barcode ?? ''}
                                                                    onChange={(event) => updateLine(line.lineId, {
                                                                        extracted: {
                                                                            ...line.extracted,
                                                                            barcode: event.target.value.trim() || null,
                                                                        },
                                                                        confirmed: true,
                                                                    })}
                                                                    sx={{...rtlInputSx, width: 105}}
                                                                    disabled={isMatchingProducts}
                                                                />
                                                            ) : null}
                                                            {extractionInputFields.hadExtId ? (
                                                                <TextField
                                                                    size="small"
                                                                    label="מזהה חיצוני"
                                                                    value={line.extracted.extId ?? ''}
                                                                    onChange={(event) => updateLine(line.lineId, {
                                                                        extracted: {
                                                                            ...line.extracted,
                                                                            extId: event.target.value.trim() || null,
                                                                        },
                                                                        confirmed: true,
                                                                    })}
                                                                    sx={{...rtlInputSx, width: 125}}
                                                                    disabled={isMatchingProducts}
                                                                />
                                                            ) : null}
                                                        </Stack>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell align="right" sx={{width: 360, position: 'relative'}}>
                                                    {isMatchingProducts ? (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                zIndex: 2,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 1,
                                                                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.82),
                                                            }}
                                                        >
                                                            <CircularProgress size={22}/>
                                                            <Typography variant="caption" color="text.secondary">
                                                                מתאים מוצר...
                                                            </Typography>
                                                        </Box>
                                                    ) : null}
                                                    <Autocomplete
                                                        fullWidth
                                                        size="small"
                                                        disabled={isMatchingProducts || isBusy}
                                                        options={lineOptions}
                                                        groupBy={(option) => candidateIdSet.has(option.productId) ? 'הצעות מותאמות' : 'כל המוצרים'}
                                                        value={line.matchedProduct ? {
                                                            productId: Number(line.matchedProduct.id),
                                                            name: line.matchedProduct.name,
                                                            unit: line.matchedProduct.unit,
                                                            availableUnits: line.matchedProduct.available_units,
                                                            catalogAvailable: isCatalogProductAvailable(line.matchedProduct),
                                                            price: line.matchedProduct.totalPrice,
                                                            barcode: line.matchedProduct.barcode,
                                                            externalId: line.matchedProduct.external_id,
                                                            priority: line.matchedProduct.priority,
                                                            score: line.confidence,
                                                        } : null}
                                                        getOptionLabel={(option) => `${option.name} (${option.productId})`}
                                                        isOptionEqualToValue={(option, value) => option.productId === value.productId}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" {...props} sx={{display: 'block !important', whiteSpace: 'normal', direction: 'rtl', textAlign: 'right', py: 0.5}}>
                                                                <Typography variant="body2" sx={{lineHeight: 1.2}}>{option.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.1}}>
                                                                    #{option.productId}
                                                                    {option.barcode ? ` | ${option.barcode}` : ''}
                                                                    {option.unit ? ` | ${option.unit}` : ''}
                                                                    {option.price !== undefined ? ` | ${option.price}` : ''}
                                                                    {option.priority !== undefined && Number(option.priority) > 0 ? ` | עדיפות ${option.priority}` : ''}
                                                                    {option.catalogAvailable === false ? ' | לא זמין' : ''}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        onChange={(_, value) => {
                                                            const matchedProduct = value ? contextProducts.find((product) => Number(product.id) === value.productId) : undefined;
                                                            updateLine(line.lineId, {
                                                                matchedProduct,
                                                                price: value?.price ?? line.price,
                                                                confidence: value?.score ?? line.confidence,
                                                                confirmed: Boolean(value),
                                                            });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={isMatchingProducts ? 'מוצר (בהתאמה...)' : 'מוצר'}
                                                                size="small"
                                                                placeholder={isMatchingProducts ? 'ממתין להתאמת AI' : undefined}
                                                                sx={{
                                                                    ...rtlInputSx,
                                                                    '& .MuiAutocomplete-input': {minWidth: '100px !important'},
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{width: 90}}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        value={line.amount}
                                                        sx={rtlInputSx}
                                                        onChange={(event) => updateLine(line.lineId, {amount: Number(event.target.value), confirmed: true})}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{width: 120}}>
                                                    <FormControl fullWidth size="small" sx={rtlInputSx}>
                                                        <Select
                                                            value={line.unitIndex}
                                                            renderValue={(value) => line.matchedProduct?.available_units?.[Number(value)] ?? line.matchedProduct?.unit ?? 'יח'}
                                                            onChange={(event) => updateLine(line.lineId, {unitIndex: Number(event.target.value), confirmed: true})}
                                                        >
                                                            {(line.matchedProduct?.available_units ?? [line.matchedProduct?.unit ?? 'יח']).map((unit, index) => (
                                                                <MenuItem value={index} key={`${line.lineId}_${unit}_${index}`}>{unit}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell align="right" sx={{width: 100}}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        value={line.price ?? 0}
                                                        sx={rtlInputSx}
                                                        onChange={(event) => updateLine(line.lineId, {price: Number(event.target.value), confirmed: true})}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{width: 150}}>
                                                    <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-start">
                                                        <Tooltip title={line.reason ?? ''}>
                                                            <Chip
                                                                size="small"
                                                                color={line.confidence >= LOW_CONFIDENCE || line.confirmed ? 'success' : 'warning'}
                                                                label={`${Math.round(line.confidence * 100)}%`}
                                                                sx={{height: 22, fontSize: 11, '& .MuiChip-label': {px: 0.75}}}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="אשר התאמה">
                                                            <IconButton
                                                                color={line.confirmed ? 'success' : 'default'}
                                                                onClick={() => updateLine(line.lineId, {confirmed: true})}
                                                                size="small"
                                                                sx={{p: 0.25}}
                                                            >
                                                                <CheckCircleIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="הסר שורה">
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => removeLine(line.lineId)}
                                                                size="small"
                                                                sx={{p: 0.25}}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : null}

                        {contextProducts.length > 0 ? (
                            <Box sx={{border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'background.paper'}}>
                                <Typography variant="subtitle2" sx={{mb: 1}}>הוסף מוצר ידנית</Typography>
                                <Stack direction={{xs: 'column', md: 'row'}} spacing={1} alignItems={{xs: 'stretch', md: 'center'}}>
                                    <Autocomplete
                                        size="small"
                                        sx={{minWidth: 280, flex: 1}}
                                        options={contextProducts}
                                        value={manualProduct}
                                        onChange={(_, value) => handleManualProductChange(value)}
                                        getOptionLabel={(option) => `${option.name} (${option.id})`}
                                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{display: 'block !important', whiteSpace: 'normal'}}>
                                                <Typography variant="body2">{option.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    #{option.id}
                                                    {option.barcode ? ` | ${option.barcode}` : ''}
                                                    {option.unit ? ` | ${option.unit}` : ''}
                                                    {option.totalPrice !== undefined ? ` | ${option.totalPrice}` : ''}
                                                    {option.priority !== undefined ? ` | עדיפות ${option.priority}` : ''}
                                                </Typography>
                                            </Box>
                                        )}
                                        renderInput={(params) => <TextField {...params} size="small" label="מוצר להוספה"/>}
                                    />
                                    <TextField
                                        size="small"
                                        sx={{width: {xs: '100%', md: 100}}}
                                        type="number"
                                        label="כמות"
                                        value={manualAmount}
                                        onChange={(event) => setManualAmount(Number(event.target.value))}
                                    />
                                    <FormControl size="small" sx={{width: {xs: '100%', md: 130}}}>
                                        <InputLabel>יחידה</InputLabel>
                                        <Select
                                            label="יחידה"
                                            value={manualUnitIndex}
                                            onChange={(event) => setManualUnitIndex(Number(event.target.value))}
                                            disabled={!manualProduct}
                                        >
                                            {(manualProduct?.available_units ?? [manualProduct?.unit ?? 'יח']).map((unit, index) => (
                                                <MenuItem value={index} key={`manual_${unit}_${index}`}>{unit}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        sx={{width: {xs: '100%', md: 110}}}
                                        type="number"
                                        label="מחיר"
                                        value={manualPrice}
                                        onChange={(event) => setManualPrice(Number(event.target.value))}
                                        disabled={!manualProduct}
                                    />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<AddIcon/>}
                                        onClick={handleAddManualProduct}
                                        disabled={!manualProduct || manualAmount <= 0}
                                        sx={{minWidth: 110}}
                                    >
                                        הוסף
                                    </Button>
                                </Stack>
                            </Box>
                        ) : null}
                    </Box>
                </Box>

                <Divider/>
                <Box sx={{p: 1.5, display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', rowGap: 1, flexShrink: 0}}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={ignoreUnavailableProducts}
                                    onChange={(event) => persistIgnoreUnavailableProducts(event.target.checked)}
                                    disabled={isBusy}
                                />
                            }
                            label={
                                <Typography variant="caption" color="text.secondary">
                                    התעלם ממוצרים לא זמינים בהתאמת AI
                                </Typography>
                            }
                            sx={{m: 0}}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={saveSourceAsNotes}
                                    onChange={(event) => persistSaveSourceAsNotes(event.target.checked)}
                                    disabled={isBusy}
                                />
                            }
                            label={
                                <Typography variant="caption" color="text.secondary">
                                    שמור טקסט מקורי כהערות במוצר{notesPrefSet ? '' : ' (יישאל בלחיצה הראשונה)'}
                                </Typography>
                            }
                            sx={{m: 0}}
                        />
                        <Typography variant="caption" color="text.secondary">
                            שורות נמוכות בטחון דורשות אישור ידני (V). ניתן לבחור ידנית מוצר לא זמין מהרשימה המלאה.
                        </Typography>
                    </Stack>
                    <Button size="small" variant="contained" disabled={!canCreate || isBusy} onClick={handlePrimaryButtonClick}>
                        {sameDayDocument
                            ? (process === 'order' ? 'יצירה / עדכון הזמנה...' : 'יצירה / עדכון חשבונית...')
                            : (process === 'order' ? 'צור הזמנה' : 'צור חשבונית ספק')}
                    </Button>
                </Box>
            </Box>
            <Dialog open={notesPrefDialogOpen} onClose={() => setNotesPrefDialogOpen(false)} dir="rtl" maxWidth="xs" fullWidth>
                <DialogTitle>שמירת הטקסט המקורי כהערות במוצר</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1}>
                        <Typography variant="body2">
                            האם לשמור את הטקסט המקורי של כל שורה (כפי שהמשתמש הקליד) כהערה על המוצר במסמך שייווצר?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            הבחירה תיזכר ותחול על כל יצירה עתידית. ניתן לשנות בכל עת בתיבת הסימון בתחתית המסך.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            persistSaveSourceAsNotes(false);
                            setNotesPrefDialogOpen(false);
                            proceedAfterNotesDecision();
                        }}
                        disabled={isBusy}
                    >
                        לא, אל תשמור
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            persistSaveSourceAsNotes(true);
                            setNotesPrefDialogOpen(false);
                            proceedAfterNotesDecision();
                        }}
                        disabled={isBusy}
                    >
                        כן, שמור כהערות
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={updatePromptOpen} onClose={() => setUpdatePromptOpen(false)} dir="rtl" maxWidth="sm" fullWidth>
                <DialogTitle>
                    {process === 'order' ? 'הזמנה קיימת באותו יום' : 'מסמך קיים באותו יום'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1}>
                        <Typography>
                            כבר קיימת {process === 'order' ? 'הזמנה' : 'חשבונית ספק'} ל{process === 'order' ? 'לקוח' : 'ספק'}
                            {' '}
                            <strong>{entity?.name ?? ''}</strong>
                            {' '}בתאריך <strong>{dateObjectToString(date)}</strong>
                            {sameDayDocument?.id ? ` (מסמך #${(sameDayDocument as any).id})` : ''}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            קיימות {((sameDayDocument as any)?.products?.length ?? 0)} שורות במסמך הקיים.
                            ב"עדכן קיים" כל {matches.length} השורות החדשות יצורפו אליו.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdatePromptOpen(false)} disabled={isBusy}>
                        בטל
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setUpdatePromptOpen(false);
                            handleCreate(false);
                        }}
                        disabled={isBusy}
                    >
                        צור חדש
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setUpdatePromptOpen(false);
                            handleCreate(true);
                        }}
                        disabled={isBusy}
                    >
                        עדכן קיים
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={debugOpen} onClose={() => setDebugOpen(false)} fullWidth maxWidth="lg" dir="rtl">
                <DialogTitle>דיבאג TextToDataDrawerAi</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {debugEvents.length === 0 ? (
                            <Typography>אין עדיין נתוני דיבאג. הרץ ניתוח קודם.</Typography>
                        ) : debugEvents.map((event, index) => (
                            <Box key={`${event.stage}_${index}`} sx={{border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5}}>
                                <Typography variant="h4" sx={{mb: 1}}>
                                    {index + 1}. {event.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    stage: {event.stage}
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        mt: 1,
                                        direction: 'ltr',
                                        textAlign: 'left',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        bgcolor: 'grey.100',
                                        p: 1.5,
                                        borderRadius: 1,
                                        maxHeight: 420,
                                        overflow: 'auto',
                                    }}
                                >
                                    {typeof event.data === 'string' ? event.data : safeDebugStringify(event.data)}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDebugEvents([])} color="warning" disabled={debugEvents.length === 0}>
                        נקה
                    </Button>
                    <Button onClick={() => setDebugOpen(false)} variant="contained">
                        סגור
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
}

